import React, { useState } from 'react';
import { Card, Spinner, Badge, Table, Tabs, Tab, Alert, Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { faSearch, faGlobe, faMapMarkerAlt, faShieldAlt, faServer, faInfoCircle, faExclamationTriangle, faNetworkWired, faChartBar, faFlag, faClock } from '@fortawesome/free-solid-svg-icons';
// import './ThreatIntelSearch.css';

const ThreatIntelSearch = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('ioc');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        try {
            let response;
            const headers = {
                'Authorization': `Bearer ${localStorage.getItem('session')}`,
                'Content-Type': 'application/json'
            };

            if (searchType === 'cve') {
                response = await fetch(`${API_URL}/cve-info/${searchQuery}`, {
                    headers: headers
                });
            } else {
                response = await fetch(`${API_URL}/threatintelligence/lookup?value=${searchQuery}`, {
                    headers: headers
                });
            }
            const data = await response.json();
            
            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch data');
            }
            
            setResult(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderSearchBar = () => (
        <div className="threat-intel__search-container mb-4">
            <div className="mb-3">
                <Tabs
                    activeKey={searchType}
                    onSelect={(k) => {
                        setSearchType(k);
                        setResult(null);
                        setError(null);
                        setSearchQuery('');
                    }}
                    className="mb-3"
                >
                    <Tab eventKey="ioc" title="IP/Domain Search">
                        <p className="text-muted">Search for IP addresses or domains to analyze their threat status</p>
                    </Tab>
                    <Tab eventKey="cve" title="CVE Search">
                        <p className="text-muted">Search for CVE information using CVE ID (e.g., CVE-2024-54676)</p>
                    </Tab>
                </Tabs>
            </div>
            <form onSubmit={handleSearch}>
                <div className="threat-intel__search-wrapper d-flex align-items-center justify-content-center">
                    <input 
                        type="text" 
                        className="search-input form-control"
                        placeholder={searchType === 'cve' ? "Enter CVE ID..." : "Enter IP address or domain to analyze..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            color: '#2c3e50',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dce1e8',
                            fontSize: '14px',
                            height: '42px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        }}
                    />
                    <button 
                        className="threat-intel__search-button btn btn-round btn-primary ml-2"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <><FontAwesomeIcon icon={faSearch} /> Analyze</>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    const renderIPDetails = () => {
        const { basic_info = {}, blacklist_info = {} } = result.data || {};
        
        return (
            <Card className="mb-4">
                <Card.Header className="d-flex align-items-center justify-content-center">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2 svgThreat" />
                    <h4 className='welcome-textSmall mb-0'>IP Intelligence Details</h4>
                            </Card.Header>
                            <Card.Body>
                    <h3 className='welcome-text'>{result.data?.query?.value || 'N/A'}</h3>
                    <div className="mb-3">
                        <Badge bg={getRiskVariant(blacklist_info.score || 0)} className="me-2">
                            Risk Score: {blacklist_info.score || 0}
                        </Badge>
                        <Badge bg="secondary">
                            Last Reported: {blacklist_info.last_reported_at 
                                                ? new Date(blacklist_info.last_reported_at).toLocaleDateString()
                                                : 'Never'}
                        </Badge>
                                </div>

                    <h5 className='welcome-textSmall'>Infrastructure Details</h5>
                    <table className="table table-custom spacing5 mb-4">
                        <tbody>
                            <tr>
                                <td className="fw-bold">ISP</td>
                                <td>{blacklist_info.isp || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="fw-bold">Usage Type</td>
                                <td>{blacklist_info.usage_type || 'N/A'}</td>
                            </tr>
                        </tbody>
                    </table>

                    <h5 className='welcome-textSmall'>Recent Reports</h5>
                        {!blacklist_info.reports?.length ? (
                        <Alert variant="info">No recent reports available.</Alert>
                        ) : (
                                <table className="table table-custom spacing5 mb-0">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Reporter</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blacklist_info.reports.slice(0, 10).map((report, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap">
                                                    {new Date(report.reportedAt).toLocaleString()}
                                                </td>
                                                <td>
                                                    {report.categories?.map(cat => (
                                                        <Badge 
                                                            key={cat} 
                                                            bg="info" 
                                                            className="me-1"
                                                        >
                                                            {getCategoryName(cat)}
                                                        </Badge>
                                                    ))}
                                                </td>
                                                <td>
                                                    <FontAwesomeIcon icon={faFlag} className="me-1" />
                                                    {report.reporterCountryName || 'Unknown'}
                                                </td>
                                                <td className="text-wrap">{report.comment || 'No details'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const renderDomainDetails = () => {
        const domainData = result.data?.detailed_data?.whois;
        
        return (
            <Card className="mb-4">
                <Card.Header className="d-flex align-items-center justify-content-center">
                    <FontAwesomeIcon icon={faGlobe} className="me-2 svgThreat" />
                    <h4 className='welcome-textSmall mb-0'>Domain Intelligence Details</h4>
                </Card.Header>
                <Card.Body>
                    <h3 className='welcome-text'>{result.data?.query?.value || 'N/A'}</h3>
                    
                    <h5 className='welcome-textSmall'>WHOIS Information</h5>
                    <table className="table table-custom spacing5 mb-4">
                        <tbody>
                            {domainData?.data.map((item, index) => (
                                <tr key={index}>
                                    <td className="fw-bold">{item.name}</td>
                                    <td>{formatWhoisValue(item)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {domainData?.related?.length > 0 && (
                        <>
                            <h5 className='welcome-textSmall'>Related Domains</h5>
                            <div className="domains-list">
                                {domainData.related.map((item, index) => (
                                    <Badge 
                                        key={index} 
                                        bg="light" 
                                        text="dark" 
                                        className="domain-badge me-2 mb-2"
                                    >
                                        {item.domain}
                                    </Badge>
                                ))}
                            </div>
                        </>
                        )}
                    </Card.Body>
                </Card>
        );
    };

    const formatWhoisValue = (item) => {
        if (item.key === 'status') {
            return <Badge bg="secondary" className="me-1 mb-1">{item.value}</Badge>;
        } else if (item.key.includes('date')) {
            return new Date(item.value).toLocaleString();
        }
        return item.value;
    };

    const getRiskClass = (score) => {
        if (score >= 75) return 'high-risk';
        if (score >= 50) return 'medium-risk';
        return 'low-risk';
    };

    const getRiskVariant = (score) => {
        if (score >= 75) return 'danger';
        if (score >= 50) return 'warning';
        return 'success';
    };

    const getCategoryName = (categoryId) => {
        const categories = {
            1: 'Spam',
            2: 'Malware',
            3: 'Phishing',
            4: 'DDoS',
            5: 'Botnet',
            6: 'Exploit',
            7: 'Fraud',
            8: 'Intrusion',
            9: 'Ransomware',
            10: 'Scanning',
            11: 'Spam',
            12: 'Suspicious',
            13: 'Abuse',
            14: 'Port Scan',
            15: 'Brute Force',
            16: 'Web Attack',
            17: 'Malware C2',
            18: 'Suspicious Traffic',
            19: 'Botnet C2',
            20: 'Unauthorized Access',
            21: 'Vulnerability',
            22: 'Malicious Activity',
            23: 'Crypto Mining'
        };
        return categories[categoryId] || `Category ${categoryId}`;
    };

    const renderCVEDetails = () => {
        const cveData = result.data;
        const cvssData = cveData.impact?.baseMetricV3_1?.cvssV3_1;
        
        return (
            <Card className="mb-4">
                <Card.Header className="d-flex align-items-center justify-content-center">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2 svgThreat" />
                    <h4 className='welcome-textSmall mb-0'>CVE Details</h4>
                </Card.Header>
                <Card.Body>
                    <h3 className='welcome-text'>{cveData.cveId}</h3>
                    <div className="mb-3">
                        {cvssData && (
                            <Badge bg={getCVESeverityColor(cvssData.baseSeverity)} className="me-2">
                                {cvssData.baseSeverity} ({cvssData.baseScore})
                            </Badge>
                        )}
                        <Badge bg="secondary">
                            {new Date(cveData.publishedDate).toLocaleDateString()}
                        </Badge>
                    </div>
                    <h5 className='welcome-textSmall'>Description</h5>
                    <p>{cveData.cveDescription}</p>
                    
                    {cvssData && (
                        <div className="mt-4">
                            <h5 className='welcome-textSmall'>CVSS v3.1 Metrics</h5>
                            <table className="table table-custom spacing5 mb-0">
                                <thead>
                                    <tr>
                                        <th>Attack Complexity</th>
                                        <th>Privileges Required</th>
                                        <th>User Interaction</th>
                                        <th>Confidentiality Impact</th>
                                        <th>Integrity Impact</th>
                                        <th>Availability Impact</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{cvssData.attackComplexity}</td>
                                        <td>{cvssData.privilegesRequired}</td>
                                        <td>{cvssData.userInteraction}</td>
                                        <td>{cvssData.confidentialityImpact}</td>
                                        <td>{cvssData.integrityImpact}</td>
                                        <td>{cvssData.availabilityImpact}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {cveData.references?.length > 0 && (
                        <>
                            <h5 className="mt-4">References</h5>
                            <ul>
                                {cveData.references.map((ref, index) => (
                                    <li key={index}>
                                        <a href={ref.url} target="_blank" rel="noopener noreferrer">
                                            {ref.url}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const getCVESeverityColor = (severity) => {
        switch (severity?.toUpperCase()) {
            case 'CRITICAL': return 'danger';
            case 'HIGH': return 'warning';
            case 'MEDIUM': return 'info';
            case 'LOW': return 'success';
            default: return 'secondary';
        }
    };

    return (
       <div className='container-fluid'>
         <div className="threat-intel">
            <div className="content-wrapper">
                <div className="block-header">
                    <h2 className='welcome-text'>Threat Intelligence Analysis</h2>
                    <p className="welcome-textSmall">Analyze IP addresses, domains, and CVEs for security insights</p>
                </div>

                {renderSearchBar()}

                {error && (
                    <Alert variant="danger">{error}</Alert>
                )}

                {loading && (
                    <div className="text-center my-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}

                {result && !error && (
                    searchType === 'cve' ? renderCVEDetails() :
                    result.data?.query?.type === 'domain' ? (
                        renderDomainDetails()
                    ) : (
                        renderIPDetails()
                    )
                )}
            </div>
        </div>
       </div>
    );
};

export default ThreatIntelSearch; 